import {z} from 'zod/v4';
import {
  Dateish,
  objectIdStringZ4,
  optionalNonNullZ4,
} from '@packages/utilities/zod';

export const recommendationTopics = [
  'metadata',
  'schema',
  'page',
  'component',
  'cms',
  'asset',
] as const;
export const RecommendationTopic = z.enum(recommendationTopics);
export type RecommendationTopic = z.infer<typeof RecommendationTopic>;

export const recommendationTypes = ['gap', 'opportunity'] as const;
export const RecommendationType = z.enum(recommendationTypes);
export type RecommendationType = z.infer<typeof RecommendationType>;

export const recommendationSourceTypes = ['site_audit'] as const;
export const RecommendationSourceType = z.enum(recommendationSourceTypes);
export type RecommendationSourceType = z.infer<typeof RecommendationSourceType>;

export const recommendationPriorities = [
  'low',
  'medium',
  'high',
  'critical',
] as const;
export const RecommendationPriority = z.enum(recommendationPriorities);
export type RecommendationPriority = z.infer<typeof RecommendationPriority>;

export const recommendationStatuses = [
  'pending',
  'in_progress',
  'active',
  'applied',
  'dismissed',
  'failed',
  'stale',
] as const;
export const RecommendationStatus = z.enum(recommendationStatuses);
export type RecommendationStatus = z.infer<typeof RecommendationStatus>;

export const recommendationRules = [
  'asset_alt_text_required',
  'page_title_required',
  'page_title_length_invalid',
  'page_title_stale',
  'page_description_required',
  'page_description_length_invalid',
  'page_description_stale',
  'page_schema_required',
  'page_schema_stale',
  'broken_link',
  'item_recommendation',
] as const;
export const RecommendationRule = z.enum(recommendationRules);
export type RecommendationRule = z.infer<typeof RecommendationRule>;

export interface TopicMetadata {
  label: string;
}

export const TOPIC_METADATA: Record<RecommendationTopic, TopicMetadata> = {
  metadata: {label: 'Metadata'},
  schema: {label: 'Schema'},
  asset: {label: 'Assets'},
  page: {label: 'Pages'},
  component: {label: 'Components'},
  cms: {label: 'Content'},
};

export interface RuleMetadata {
  topic: RecommendationTopic;
  type: RecommendationType;
  priority: RecommendationPriority;
  label: string;
  /** Whether the rule is enabled by default when not set in site ruleConfig. */
  defaultEnabled: boolean;
}

export const RULE_METADATA: Record<RecommendationRule, RuleMetadata> = {
  asset_alt_text_required: {
    topic: 'asset',
    type: 'gap',
    priority: 'high',
    label: 'Missing alt text',
    defaultEnabled: true,
  },
  page_title_required: {
    topic: 'metadata',
    type: 'gap',
    priority: 'high',
    label: 'Missing meta title',
    defaultEnabled: true,
  },
  page_title_length_invalid: {
    topic: 'metadata',
    type: 'opportunity',
    priority: 'medium',
    label: 'Meta title is too long',
    defaultEnabled: true,
  },
  page_title_stale: {
    topic: 'metadata',
    type: 'opportunity',
    priority: 'low',
    label: 'Meta title may be outdated',
    defaultEnabled: true,
  },
  page_description_required: {
    topic: 'metadata',
    type: 'gap',
    priority: 'high',
    label: 'Meta description missing',
    defaultEnabled: true,
  },
  page_description_length_invalid: {
    topic: 'metadata',
    type: 'opportunity',
    priority: 'medium',
    label: 'Meta description is too long',
    defaultEnabled: true,
  },
  page_description_stale: {
    topic: 'metadata',
    type: 'opportunity',
    priority: 'low',
    label: 'Meta description may be outdated',
    defaultEnabled: true,
  },
  page_schema_required: {
    topic: 'schema',
    type: 'gap',
    priority: 'medium',
    label: 'Missing schema markup',
    defaultEnabled: true,
  },
  page_schema_stale: {
    topic: 'schema',
    type: 'opportunity',
    priority: 'low',
    label: 'Schema markup may be outdated',
    defaultEnabled: true,
  },
  broken_link: {
    topic: 'page',
    type: 'gap',
    priority: 'high',
    label: 'Broken links',
    defaultEnabled: true,
  },
  item_recommendation: {
    topic: 'cms',
    type: 'opportunity',
    priority: 'low',
    label: 'Content recommendation',
    defaultEnabled: true,
  },
};

export const recommendationCategories = [
  'Accessibility',
  'Discoverability',
  'Site reliability',
] as const;
export type RecommendationCategory = (typeof recommendationCategories)[number];

export const TOPIC_TO_CATEGORY: Record<
  RecommendationTopic,
  RecommendationCategory
> = {
  asset: 'Accessibility',
  metadata: 'Discoverability',
  schema: 'Discoverability',
  cms: 'Discoverability',
  page: 'Discoverability',
  component: 'Discoverability',
};

export const CollectionReference = z.object({
  id: objectIdStringZ4,
  itemId: optionalNonNullZ4(objectIdStringZ4),
  fieldSlug: optionalNonNullZ4(z.string()),
  fileId: optionalNonNullZ4(objectIdStringZ4),
});
export type CollectionReference = z.infer<typeof CollectionReference>;

export const RecommendationSource = z.object({
  type: RecommendationSourceType,
  id: objectIdStringZ4,
  name: optionalNonNullZ4(z.string()),
  runOn: optionalNonNullZ4(Dateish),
});
export type RecommendationSource = z.infer<typeof RecommendationSource>;

export const WorkflowRunMeta = z.object({
  id: objectIdStringZ4,
  runId: z.string(),
  status: optionalNonNullZ4(z.string()),
  startedAt: optionalNonNullZ4(z.string()),
  stoppedAt: optionalNonNullZ4(z.string()),
});
export type WorkflowRunMeta = z.infer<typeof WorkflowRunMeta>;

export const NonEmptyJsonLdSchema = z
  .record(z.string(), z.unknown())
  .refine((schema) => Object.keys(schema).length > 0, {
    message: 'Generated JSON-LD schema must contain at least one property',
  });
export type NonEmptyJsonLdSchema = z.infer<typeof NonEmptyJsonLdSchema>;

export const SchemaRecommendationProposal = z.object({
  jsonLdSchema: NonEmptyJsonLdSchema,
});
export type SchemaRecommendationProposal = z.infer<
  typeof SchemaRecommendationProposal
>;

export const SiteRecommendation = z.object({
  id: objectIdStringZ4,
  source: RecommendationSource,
  siteId: objectIdStringZ4,
  localeId: optionalNonNullZ4(objectIdStringZ4),
  pageId: optionalNonNullZ4(objectIdStringZ4),
  collection: optionalNonNullZ4(CollectionReference),
  // Relative Agent Instruction path used for content-brief brand context.
  // Persisted so a failed brief can be retried with the same instruction.
  agentInstructionPath: optionalNonNullZ4(z.string().min(1)),
  elementId: optionalNonNullZ4(z.string()),
  componentId: optionalNonNullZ4(objectIdStringZ4),
  assetId: optionalNonNullZ4(objectIdStringZ4),
  assetUrl: optionalNonNullZ4(z.string()),
  fileHash: optionalNonNullZ4(z.string()),
  priority: RecommendationPriority,
  topic: RecommendationTopic,
  type: RecommendationType,
  rule: RecommendationRule,
  evidence: optionalNonNullZ4(z.string()),
  action: optionalNonNullZ4(z.string()),
  proposal: optionalNonNullZ4(z.record(z.string(), z.unknown())),
  status: RecommendationStatus,
  actionedBy: optionalNonNullZ4(objectIdStringZ4),
  actionedOn: optionalNonNullZ4(Dateish),
  seenOn: optionalNonNullZ4(Dateish),
  feedbackSubmittedOn: optionalNonNullZ4(Dateish),
  errorMessage: optionalNonNullZ4(z.string()),
  retryCount: optionalNonNullZ4(z.number().int().min(0)),
  wf: optionalNonNullZ4(z.record(z.string(), WorkflowRunMeta)),
  createdOn: Dateish,
  updatedOn: Dateish,
});
export type SiteRecommendation = z.infer<typeof SiteRecommendation>;

/**
 * Fields that may be mutated on an existing recommendation.
 * Identity fields (id, siteId, rule, topic, type, createdOn)
 * are intentionally excluded.
 */
export const UpdateSiteRecommendationFields = SiteRecommendation.pick({
  status: true,
  action: true,
  proposal: true,
  evidence: true,
  priority: true,
  errorMessage: true,
  retryCount: true,
  wf: true,
  actionedBy: true,
  actionedOn: true,
  fileHash: true,
  agentInstructionPath: true,
  updatedOn: true,
})
  .extend({
    collection: z
      .object({
        id: z.string().optional(),
        itemId: z.string().optional(),
        fieldSlug: z.string().optional(),
      })
      .optional(),
  })
  .partial();
export type UpdateSiteRecommendationFields = z.infer<
  typeof UpdateSiteRecommendationFields
>;

export const CreateSiteRecommendationInput = SiteRecommendation.omit({
  id: true,
  topic: true,
  type: true,
  priority: true,
  createdOn: true,
  updatedOn: true,
  status: true,
  actionedBy: true,
  actionedOn: true,
  seenOn: true,
  feedbackSubmittedOn: true,
}).extend({
  priority: RecommendationPriority.optional(),
  // Not persisted. When true, the upsert treats a stamped primary row and a
  // legacy locale-less row as the same record. Can be true with no localeId
  // when locale lookup failed — we still target primary, just can't stamp it.
  // Without primaryLocaleId the upsert can't identify the primary row and
  // falls back to per-target disambiguation (probe + insert suppression).
  // primaryLocaleId is the id used for that match.
  targetsPrimaryLocale: z.boolean().optional(),
  primaryLocaleId: objectIdStringZ4.optional(),
});
export type CreateSiteRecommendationInput = z.infer<
  typeof CreateSiteRecommendationInput
>;

export const RecommendationCountByRule = z.object({
  rule: RecommendationRule,
  type: RecommendationType,
  priority: RecommendationPriority,
  label: z.string(),
  count: z.number(),
});
export type RecommendationCountByRule = z.infer<
  typeof RecommendationCountByRule
>;

export const RecommendationCountByTopic = z.object({
  topic: RecommendationTopic,
  rules: z.array(RecommendationCountByRule),
});
export type RecommendationCountByTopic = z.infer<
  typeof RecommendationCountByTopic
>;

export const RecommendationCountByType = z.object({
  type: RecommendationType,
  rules: z.array(RecommendationCountByRule),
});
export type RecommendationCountByType = z.infer<
  typeof RecommendationCountByType
>;

/**
 * Counts of recommendations for a site grouped by `RecommendationStatus`.
 * Every status key is always present (zero-filled) so the FE can render
 * counts without branching on undefined. Surfaced via
 * `GET /sites/:site/recommendations/summary/by-status` so the recommendations
 * panel can show, for example, "X active, Y pending" after a capped
 * proposal-generation run (see AEO-690).
 *
 * Defined as an explicit object literal (rather than a dynamic build over
 * `recommendationStatuses`) so adding a new `RecommendationStatus` value
 * triggers a type-level failure here, surfacing the registry-update
 * obligation across this schema, the repository's zero-fill, and any FE
 * consumer of the response.
 */
export const RecommendationCountByStatus = z.object({
  pending: z.number(),
  in_progress: z.number(),
  active: z.number(),
  applied: z.number(),
  dismissed: z.number(),
  failed: z.number(),
  stale: z.number(),
}) satisfies z.ZodObject<{[K in RecommendationStatus]: z.ZodNumber}>;
export type RecommendationCountByStatus = z.infer<
  typeof RecommendationCountByStatus
>;

export const MAX_SEO_TITLE_LENGTH = 60;
export const MAX_SEO_DESC_LENGTH = 160;

/**
 * Max UTF-16 length for a user-edited AEO content brief markdown payload.
 * Shared FE/BE so the client can reject an over-length brief before it ever
 * hits the server's `UpdateBriefContentDto` validation.
 */
export const MAX_BRIEF_CONTENT_LENGTH = 100_000;

export const RECONCILABLE_PAGE_FIELDS = [
  'seoTitle',
  'seoDesc',
  'jsonLdSchema',
] as const;
export type ReconcilablePageField = (typeof RECONCILABLE_PAGE_FIELDS)[number];

export type PageSeoFieldValues = Partial<
  Record<ReconcilablePageField, unknown>
>;

export const BrokenLinkClientPayload = z.discriminatedUnion('type', [
  z.object({type: z.literal('page'), pageId: z.string()}),
  z.object({
    type: z.literal('collection-item'),
    itemId: z.string(),
    collectionId: z.string(),
  }),
  z.object({type: z.literal('url'), url: z.string().url()}),
]);
export type BrokenLinkClientPayload = z.infer<typeof BrokenLinkClientPayload>;

export const PageSettingsClientPayload = z.object({
  seoTitle: z.string().optional(),
  seoDesc: z.string().optional(),
});
export type PageSettingsClientPayload = z.infer<
  typeof PageSettingsClientPayload
>;

export const AssetAltTextClientPayload = z.object({
  altText: z.string(),
});
export type AssetAltTextClientPayload = z.infer<
  typeof AssetAltTextClientPayload
>;

export const ApplyProposalClientPayload = z
  .object({
    broken_link: BrokenLinkClientPayload.optional(),
    page_settings: PageSettingsClientPayload.optional(),
    asset_alt_text: AssetAltTextClientPayload.optional(),
  })
  .strict();
export type ApplyProposalClientPayload = z.infer<
  typeof ApplyProposalClientPayload
>;

export const RecommendationEventDetail = z.object({
  id: z.string(),
  status: z.string(),
  rule: z.string(),
});

export const RecommendationChangedPayload = z.object({
  details: z.array(RecommendationEventDetail),
});
export type RecommendationChangedPayload = z.infer<
  typeof RecommendationChangedPayload
>;
